import styled, { keyframes } from 'styled-components';
import {
  SYS_BORDER_RADIUS_MD,
  SYS_BORDER_WIDTH_100,
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
} from '@oneaudi/unified-web-common';

import type { LayerTypes } from './globals';
import {
  ALERT,
  FOCUS_LARGE,
  FOCUS_SMALL,
  FULLSCREEN,
  oneLayerTimingsConfig as timeConfig,
} from './globals';
import { breakpoints } from '../utils/breakpoints';
import { responsiveStyles } from '../utils/responsiveStyles';

interface Sizes {
  m?: string;
  l?: string;
  xs?: string;
}

interface Itypes {
  'max-width'?: Sizes;
  width: Sizes;
}
interface IstylesInner {
  [ALERT]: Itypes;
  [FOCUS_LARGE]: Itypes;
  [FOCUS_SMALL]: Itypes;
  [FULLSCREEN]: Itypes;
}

const stylesInner = (): IstylesInner => ({
  [ALERT]: {
    'max-width': {
      m: '600px',
    },
    width: {
      xs: '100%',
    },
  },
  [FOCUS_LARGE]: {
    width: {
      l: SYS_BREAKPOINT_LG,
      xs: '100%',
    },
  },
  [FOCUS_SMALL]: {
    width: {
      m: SYS_BREAKPOINT_MD,
      xs: '100%',
    },
  },
  [FULLSCREEN]: {
    width: {
      xs: '100%',
    },
  },
});

const openKeyFramesSmall = keyframes`
  0% {
    transform: translateY(100%);
  }

  100% {
    transform: translateY(0);
  }
`;

const openKeyFramesLarge = keyframes`
  0% {
    transform: translateY(40px);
  }

  100% {
    transform: translateY(0px);
  }
`;

const hideKeyFrames = keyframes`
  0% {
    transform: scale(0.9, 0.9) translateY(40px);
  }

  100% {
    transform: scale(1, 1) translateY(0px);
  }
`;

const showKeyFrames = keyframes`
  0% {
    transform: scale(0.9, 0.9) translateY(40px);
  }

  100% {
    transform: scale(1, 1) translateY(0px);
  }
`;

const opacityKeyFrames = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

export const AnimationWrapper = styled.div<IOneLayerInner>`
  max-height: 100%;

  ${({ type }): string =>
    stylesInner()[type] && responsiveStyles({ width: stylesInner()[type].width }, breakpoints)};

  .oneLayerShowAnimation {
    animation-delay: 0s, 0s;
    animation-duration: ${timeConfig.XL}ms, ${timeConfig.L}ms;
    animation-fill-mode: forwards;
    animation-name: ${showKeyFrames}, ${opacityKeyFrames};
    animation-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
  }

  .oneLayerHideAnimation {
    animation-delay: 0s, ${timeConfig.M}ms;
    animation-direction: reverse;
    animation-duration: ${timeConfig.XL}ms, ${timeConfig.L}ms;
    animation-fill-mode: forwards;
    animation-name: ${hideKeyFrames}, ${opacityKeyFrames};
    animation-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
  }

  .oneLayerOpenAnimation {
    animation-duration: ${timeConfig.XL}ms;
    animation-fill-mode: forwards;
    animation-name: ${openKeyFramesSmall};
    animation-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
    @media (min-width: ${breakpoints.m}px) {
      animation-duration: ${timeConfig.XL}ms, ${timeConfig.L}ms;
      animation-name: ${openKeyFramesLarge}, ${opacityKeyFrames};
    }
  }
`;
interface IOneLayerInner {
  type: LayerTypes;
}
const OneLayerInner = styled.dialog<IOneLayerInner>`
  ${({ theme }) => `
      background:  ${process.env.IS_LEGACY ? SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT : theme?.sys?.gradient?.elevation?.level1?.fill};
       border: ${
         process.env.IS_LEGACY
           ? 'none'
           : `${SYS_BORDER_WIDTH_100} solid ${theme?.sys?.color?.elevation?.level1?.stroke}`
       };
  `};
  display: flex;
  --page-margin: 0;
  flex-direction: column;
  justify-content: center;
  max-height: 100%;
  position: relative;
  width: 100%;
  padding: 0;

  ${({ type }): string => (type === 'FULLSCREEN' ? 'height: 100vh;' : '')}

  /* stylelint-disable-next-line order/properties-alphabetical-order */
  ${({ type, theme }): string =>
    (stylesInner()[type] &&
      stylesInner()[type]['max-width'] &&
      responsiveStyles(
        {
          // @ts-expect-error TODO: revisit?
          'max-width': stylesInner(theme)[type]['max-width'],
        },
        breakpoints,
      )) ||
    ''}
    @media (min-width: ${breakpoints.m}px) {
    ${({ type }): string =>
      type === 'ALERT'
        ? `border-radius: ${process.env.IS_LEGACY ? 'none' : SYS_BORDER_RADIUS_MD};`
        : ''}
  }

  @media (max-width: ${breakpoints.m}px) {
    border-radius: ${process.env.IS_LEGACY
      ? '0 0 0 0'
      : `${SYS_BORDER_RADIUS_MD} ${SYS_BORDER_RADIUS_MD} 0 0`};
  }
  @media (min-width: ${breakpoints.l}px) {
    border-radius: ${process.env.IS_LEGACY ? 'none' : SYS_BORDER_RADIUS_MD};
  }
`;

export const Animation = styled.div`
  display: flex;
  justify-content: center;
  max-height: 100%;
  width: 100%;
`;

export const ScrollableContent = styled.div`
  max-height: 100vh;
  overflow-y: auto;
`;

export default OneLayerInner;
